import { jsxs, jsx } from "react/jsx-runtime";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils.js";
import KatexRenderer from "../../../utils/KatexRenderer.js";
import { formatProblem } from "../../../utils/quiz-parser.js";
import "../shared/quiz-blank.js";
import "../../../node_modules/.pnpm/katex@0.10.2/node_modules/katex/dist/katex.js";
/* empty css                                                                             */
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import "../shared/quiz-style-context.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { useExplanationQuiz } from "../hooks/use-explanation-quiz.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
function ExplanationQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false
}) {
  var _a;
  const { t } = useTranslation("quiz");
  const {
    answer,
    setAnswer,
    isSolved,
    canSubmit,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit
  } = useExplanationQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const renderedProblem = useMemo(() => {
    if (!quiz.problem) return [];
    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      () => null,
      quiz.additionalImages ?? void 0
    );
  }, [quiz.problem, quiz.additionalImages, quizId]);
  const correctAnswer = ((_a = solveResult == null ? void 0 : solveResult.correctAnswers) == null ? void 0 : _a.join(", ")) ?? "";
  const displayedAnswer = isViewSolved && correctAnswer ? correctAnswer : answer;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.explanation.label", "서술형"),
        title: quiz.title,
        difficultyLevel,
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars
      }
    ),
    renderedProblem.length > 0 && /* @__PURE__ */ jsx(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        "data-quiz-problem-text": true,
        className: cn(
          "p-4 rounded-xl bg-gray-100",
          "text-xl text-gray-700",
          "[&_.formatProblem]:leading-relaxed"
        ),
        children: renderedProblem
      }
    ),
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { delay: 0.1 },
        children: [
          isSolved || isViewSolved ? /* @__PURE__ */ jsx(
            "div",
            {
              "data-testid": "explanation-answer",
              className: "min-h-[180px] w-full p-4 rounded-xl border-2 border-gray-200 bg-gray-50 text-lg text-gray-700 whitespace-pre-wrap break-words",
              children: /* @__PURE__ */ jsx(KatexRenderer, { text: displayedAnswer })
            }
          ) : /* @__PURE__ */ jsx(
            "textarea",
            {
              value: displayedAnswer,
              onChange: (e) => setAnswer(e.target.value),
              placeholder: t("types.explanation.placeholder", "답변을 입력하세요"),
              maxLength: 500,
              rows: 5,
              className: cn(
                "w-full p-4 rounded-xl border-2 text-lg text-gray-700 resize-y transition-colors outline-hidden",
                "bg-white border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              )
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "mt-1 text-right text-sm text-gray-400", children: [
            displayedAnswer.length,
            "/500"
          ] })
        ]
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? null,
        show: isSolved,
        disableSound,
        isViewSolved
      }
    ),
    !isViewSolved && isSolved && (solveResult == null ? void 0 : solveResult.correctAnswers) && solveResult.correctAnswers.length > 0 && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 10 },
        animate: { opacity: 1, y: 0 },
        className: "p-4 rounded-xl bg-blue-50 border border-blue-200",
        children: [
          /* @__PURE__ */ jsx(
            "button",
            {
              onClick: () => setIsShowingCorrectAnswers((v) => !v),
              className: "text-sm font-semibold text-blue-700 hover:text-blue-800",
              children: isShowingCorrectAnswers ? t("common.viewMyAnswer", "내 답안") : t("common.viewCorrectAnswer", "모범답안 보기")
            }
          ),
          isShowingCorrectAnswers && /* @__PURE__ */ jsx("div", { className: "mt-2 text-base text-blue-900", children: /* @__PURE__ */ jsx(KatexRenderer, { text: solveResult.correctAnswers.join(", ") }) })
        ]
      }
    ),
    isSolved && (solveResult == null ? void 0 : solveResult.description) && !hideExplanation && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: solveResult.description,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  ExplanationQuizAdult
};
//# sourceMappingURL=explanation-adult.js.map
